/**
 * Second look at a board photo, rail by rail. On a whole board the devices are small, and a model
 * can misread one it would read right close up: on Qwen 3.8, RCBOs (C16 + 30 mA) came back as
 * RCDs with no curve, yet a crop of their rail was read right. So when the first reading has a
 * device that looks misread, the rail's band of the photo is read again and those devices are
 * taken from the close-up.
 *
 * Bands are cut from the row count alone (rails are evenly spread in a straight-on photo), with a
 * margin so a rail is never cut: the close-up may show parts of the neighbouring rails, so its
 * devices are matched to the first reading by label, not by position.
 */
import type { BoardDevice, BoardExtraction } from './schema.ts';

/** An "RCD" that carries a circuit label, or a breaker-sized rating with 30 mA: likely an RCBO. */
export function looksMisread(d: BoardDevice): boolean {
  if (d.deviceType !== 'RCD') return false;
  const breakerSized = d.ratingA !== null && d.ratingA <= 20 && (d.sensitivityMa ?? 300) <= 30;
  return !!d.label || breakerSized;
}

/** Rails to read again, top first. */
export function railsToRecheck(ex: BoardExtraction): number[] {
  return [...new Set(ex.devices.filter(looksMisread).map((d) => d.row))].sort((a, b) => a - b);
}

/** The band of the photo (fractions of its height) that holds rail `row` of `rows`. */
export function railBand(row: number, rows: number, margin = 0.08): { top: number; bottom: number } {
  return {
    top: Math.max(0, (row - 1) / rows - margin),
    bottom: Math.min(1, row / rows + margin),
  };
}

const norm = (s: string | null) =>
  (s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '');

/**
 * Take the misread devices of rail `row` from its close-up, matched by label. Returns the new
 * extraction and how many devices changed.
 */
export function mergeRailReading(
  ex: BoardExtraction,
  row: number,
  closeUp: BoardExtraction,
): { extraction: BoardExtraction; changed: string[] } {
  const changed: string[] = [];
  const devices = ex.devices.map((d) => {
    if (d.row !== row || !looksMisread(d) || !d.label) return d;
    const twins = closeUp.devices.filter((c) => norm(c.label) === norm(d.label));
    const c = twins.length === 1 ? twins[0]! : undefined;
    if (!c || c.deviceType === d.deviceType) return d;
    changed.push(d.label);
    return {
      ...d,
      deviceType: c.deviceType,
      ratingA: c.ratingA,
      curve: c.curve,
      poles: c.poles,
      sensitivityMa: c.sensitivityMa,
      rcdType: c.rcdType,
      confidence: c.confidence,
    };
  });
  return { extraction: { ...ex, devices }, changed };
}

/**
 * Re-read the rails that look misread. `readBand` reads the band of the photo between two
 * fractions of its height (the app crops the image and calls the board endpoint again).
 */
export async function refineExtraction(
  ex: BoardExtraction,
  readBand: (top: number, bottom: number) => Promise<BoardExtraction>,
  note: (rail: number, labels: string[]) => string = (r, l) =>
    `Rail ${r} read again close up: ${l.join(', ')} corrected.`,
): Promise<{ extraction: BoardExtraction; rechecked: number[] }> {
  const rows = Math.max(0, ...ex.devices.map((d) => d.row));
  const rechecked = railsToRecheck(ex);
  let out = ex;
  const notes: string[] = [];
  for (const row of rechecked) {
    const band = railBand(row, rows);
    let closeUp: BoardExtraction;
    try {
      closeUp = await readBand(band.top, band.bottom);
    } catch {
      continue; // the first reading stands; the rules still flag what is missing
    }
    const merged = mergeRailReading(out, row, closeUp);
    out = merged.extraction;
    if (merged.changed.length) notes.push(note(row, merged.changed));
  }
  return {
    extraction: notes.length ? { ...out, notes: [out.notes, ...notes].filter(Boolean).join(' ') } : out,
    rechecked,
  };
}
